import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  LinearProgress,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Analytics,
  TrendingUp,
  TrendingDown,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import useMlPredictions from '../hooks/useMlPredictions';
import useLiveGoldPrice from '../hooks/useLiveGoldPrice';

const fmt = (n) =>
  n == null || Number.isNaN(n)
    ? '—'
    : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Technical Analysis aligned with live spot + ML forecast
 * (no conflicting Buy vs Down)
 */
const TechnicalAnalysis = () => {
  const { data, loading, error } = useMlPredictions();
  const live = useLiveGoldPrice(30000);

  const indicators = useMemo(() => {
    if (!data?.predictions) return null;

    const last = data.current || {};
    const r = data.returns || {};
    const p = data.predictions;

    const liveSpot = live.price ?? data.live?.price ?? last.close;
    if (liveSpot == null) return null;

    // Apply ML returns to live (same as forecast panels)
    const dayEnd = r.dayEndClose != null ? liveSpot * (1 + r.dayEndClose) : p.dayEndClose;
    const nextClose = r.nextDayClose != null ? liveSpot * (1 + r.nextDayClose) : p.nextDayClose;
    let nextHigh = r.nextDayHigh != null ? liveSpot * (1 + r.nextDayHigh) : p.nextDayHigh;
    let nextLow = r.nextDayLow != null ? liveSpot * (1 + r.nextDayLow) : p.nextDayLow;
    nextHigh = Math.max(nextHigh, nextClose, liveSpot);
    nextLow = Math.min(nextLow, nextClose, liveSpot);

    const todayDir = dayEnd >= liveSpot ? 'UP' : 'DOWN';
    const tomDir = nextClose >= liveSpot ? 'UP' : 'DOWN';
    const todayPct = ((dayEnd - liveSpot) / liveSpot) * 100;
    const tomPct = ((nextClose - liveSpot) / liveSpot) * 100;

    // Momentum vs last completed session close
    const vsLastClose = last.close != null ? liveSpot - last.close : null;
    const vsLastPct = last.close ? (vsLastClose / last.close) * 100 : null;

    // Where live sits in tomorrow's expected range (0–100)
    const rangeSpan = nextHigh - nextLow;
    const posInRange = rangeSpan > 0 ? ((liveSpot - nextLow) / rangeSpan) * 100 : 50;

    // Momentum score: how stretched vs range
    let momentumLabel = 'Mid-range';
    let momentumColor = 'default';
    if (posInRange >= 70) {
      momentumLabel = 'Near resistance';
      momentumColor = 'warning';
    } else if (posInRange <= 30) {
      momentumLabel = 'Near support';
      momentumColor = 'info';
    }

    // Signal = aligned with ML (majority of today + tomorrow)
    let score = 0;
    if (todayDir === 'UP') score += 1;
    else score -= 1;
    if (tomDir === 'UP') score += 2;
    else score -= 2; // tomorrow close weighs more
    if (vsLastPct != null) {
      if (vsLastPct > 0.3) score += 1;
      else if (vsLastPct < -0.3) score -= 1;
    }

    let strength = 'Neutral';
    let color = 'default';
    if (score >= 2) {
      strength = 'Buy';
      color = 'success';
    } else if (score <= -2) {
      strength = 'Sell';
      color = 'error';
    } else if (score > 0) {
      strength = 'Weak Buy';
      color = 'primary';
    } else if (score < 0) {
      strength = 'Weak Sell';
      color = 'warning';
    }

    // Bias text matches forecast
    const biasDir = tomDir;
    const biasStatus = biasDir === 'UP' ? 'Bullish bias' : 'Bearish bias';

    return {
      liveSpot,
      last,
      dayEnd,
      nextClose,
      nextHigh,
      nextLow,
      todayDir,
      tomDir,
      todayPct,
      tomPct,
      vsLastClose,
      vsLastPct,
      posInRange: Math.round(Math.min(100, Math.max(0, posInRange))),
      momentumLabel,
      momentumColor,
      biasStatus,
      biasDir,
      signal: { strength, color, score },
      confidence: p.confidence ?? 70,
      support: nextLow,
      resistance: nextHigh,
    };
  }, [data, live.price]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error || !indicators) {
    return <Alert severity="warning">{error || 'No technical data'}</Alert>;
  }

  const i = indicators;
  const sellish = i.signal.strength.includes('Sell');

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Signal follows live spot + ML forecast (same as Today / Tomorrow panels)
      </Alert>

      {/* Main signal */}
      <Paper
        elevation={3}
        sx={{
          p: 2,
          mb: 2,
          textAlign: 'center',
          background: `linear-gradient(135deg, ${sellish ? '#3a1a1a' : '#1a3a1a'} 0%, #1A1A1A 100%)`,
          border: `2px solid ${sellish ? '#ff4444' : '#00C853'}`,
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Technical Signal
        </Typography>
        <Chip
          icon={<Analytics />}
          label={i.signal.strength}
          color={i.signal.color}
          size="large"
          sx={{ fontSize: '1.1rem', py: 1, fontWeight: 'bold' }}
        />
        <Box mt={1.5} display="flex" justifyContent="center" gap={1} flexWrap="wrap">
          <Chip
            size="small"
            icon={i.tomDir === 'UP' ? <ArrowUpward /> : <ArrowDownward />}
            label={`ML bias: ${i.biasStatus}`}
            color={i.tomDir === 'UP' ? 'success' : 'error'}
            variant="outlined"
          />
          <Chip size="small" label={`Score ${i.signal.score}`} variant="outlined" />
          <Chip size="small" label={`Confidence ${i.confidence}%`} color="success" variant="outlined" />
        </Box>
      </Paper>

      {/* Live vs last session */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#1A1A1A', border: '1px solid #333' }}>
            <Typography variant="body2" color="text.secondary">Live Spot</Typography>
            <Typography variant="h5" fontWeight="bold" color="primary.main">
              {fmt(i.liveSpot)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#1A1A1A', border: '1px solid #333' }}>
            <Typography variant="body2" color="text.secondary">vs Last Close</Typography>
            <Box display="flex" justifyContent="center" alignItems="center" gap={0.5}>
              {i.vsLastPct != null && i.vsLastPct >= 0 ? (
                <TrendingUp color="success" />
              ) : (
                <TrendingDown color="error" />
              )}
              <Typography
                variant="h5"
                fontWeight="bold"
                color={i.vsLastPct >= 0 ? 'success.main' : 'error.main'}
              >
                {i.vsLastPct == null
                  ? '—'
                  : `${i.vsLastPct >= 0 ? '+' : ''}${i.vsLastPct.toFixed(2)}%`}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Last session: {fmt(i.last.close)} ({i.last.date || '—'})
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#1A1A1A', border: '1px solid #333' }}>
            <Typography variant="body2" color="text.secondary">Range Position</Typography>
            <Typography variant="h5" fontWeight="bold">{i.posInRange}%</Typography>
            <Chip size="small" label={i.momentumLabel} color={i.momentumColor} variant="outlined" sx={{ mt: 0.5 }} />
            <LinearProgress
              variant="determinate"
              value={i.posInRange}
              sx={{ mt: 1, height: 8, borderRadius: 4 }}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Today / Tomorrow technical summary */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              bgcolor: '#1A1A1A',
              border: `1px solid ${i.todayDir === 'UP' ? '#00C853' : '#ff4444'}`,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Today (Day-End)
            </Typography>
            <Chip
              size="small"
              icon={i.todayDir === 'UP' ? <ArrowUpward /> : <ArrowDownward />}
              label={`${i.todayDir} ${i.todayPct >= 0 ? '+' : ''}${i.todayPct.toFixed(2)}%`}
              color={i.todayDir === 'UP' ? 'success' : 'error'}
              sx={{ mb: 1 }}
            />
            <Typography variant="h5" fontWeight="bold">
              {fmt(i.dayEnd)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Expected day-end close
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              bgcolor: '#1A1A1A',
              border: `1px solid ${i.tomDir === 'UP' ? '#00C853' : '#ff4444'}`,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Tomorrow (Close)
            </Typography>
            <Chip
              size="small"
              icon={i.tomDir === 'UP' ? <ArrowUpward /> : <ArrowDownward />}
              label={`${i.tomDir} ${i.tomPct >= 0 ? '+' : ''}${i.tomPct.toFixed(2)}%`}
              color={i.tomDir === 'UP' ? 'success' : 'error'}
              sx={{ mb: 1 }}
            />
            <Typography variant="h5" fontWeight="bold">
              {fmt(i.nextClose)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Expected next-day close
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Support / Resistance */}
      <Paper sx={{ p: 2, bgcolor: '#1A1A1A', border: '1px solid #333' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Support & Resistance (ML range)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box textAlign="center">
              <Typography variant="caption" color="error.main">Support (Low)</Typography>
              <Typography variant="h6" fontWeight="bold" color="error.main">
                {fmt(i.support)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box textAlign="center">
              <Typography variant="caption" color="success.main">Resistance (High)</Typography>
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {fmt(i.resistance)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="caption" color="text.secondary" display="block" mt={2} textAlign="center">
        Technical signal is aligned with ML Today / Tomorrow forecast · Not financial advice
      </Typography>
    </Box>
  );
};

export default TechnicalAnalysis;
